// Wachtrij voor vierogen (stap H): voorstellen die op een tweede bevoegde
// wachten, eigen voorstellen, en het beleid per handeling.
//
// Wat hier gebeurt, beslist de database: public.beslis controleert
// functiescheiding (niet je eigen voorstel, geen tweede account van dezelfde
// persoon) en voert uit in één transactie met het auditlog.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { esc, fmtMoney } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { billedOf, balanceOf } from '../lib/money.js';

const sb = () => getClient();

export function dispatchFout(e) {
  const m = String(e?.message || e || '').match(/dispatch:([a-z_]+)/);
  return m ? t('dispatch.fout.' + m[1]) : t('alg.fout', { melding: e?.message || String(e) });
}

function wanneer(iso) {
  return iso ? new Date(iso).toLocaleString(locale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

// Aantal voorstellen waar jij over mag beslissen, als teller in het menu.
export async function vernieuwTeller() {
  const el = document.getElementById('badgeGoedkeuringen');
  if (!el || !state.currentOrgId || !heeft('approval.decide')) return;
  const { data, error } = await sb().from('approval_overzicht').select('id')
    .eq('organisation_id', state.currentOrgId).eq('status', 'open').eq('mag_beslissen', true);
  const n = error ? 0 : (data || []).length;
  el.textContent = n;
  el.style.display = n ? '' : 'none';
}

export async function openVoorObject(teamId) {
  const { data, error } = await sb().from('approval_overzicht').select('action_type, submitted_by, status, payload')
    .eq('organisation_id', state.currentOrgId).eq('status', 'open');
  if (error) return [];
  return (data || []).filter(v => v.payload?.team_id === teamId);
}

// Wat een voorstel nu zou doen, uitgerekend op de huidige stand van de
// inschrijving. De database rekent bij het uitvoeren opnieuw; dit is uitleg.
const eur = n => '€' + fmtMoney(n);

const dag = iso => iso ? new Date(iso + 'T00:00:00Z').toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '';

// Inschrijvingen waar voorstellen over gaan. In de module Goedkeuringen is het
// toernooi niet geladen; dan halen we alleen die inschrijvingen op.
let teams = {};
async function laadTeams(voorstellen) {
  teams = Object.fromEntries((state.teams || []).map(x => [x.id, x]));
  const ids = [...new Set(voorstellen.map(v => v.payload?.team_id).filter(id => id && !teams[id]))];
  if (!ids.length) return;
  const { data } = await sb().from('teams').select('*').in('id', ids);
  for (const x of data || []) teams[x.id] = x;
}

function effect(v) {
  const g = v.payload || {};
  const detail = v.object_detail || '';
  switch (v.action_type) {
    case 'grade.set': return t('vierogen.effect.graad', { graad: detail, datum: dag(g.datum) || t('bond.vandaag') });
    case 'grade.revoke': return t('vierogen.effect.graad_weg', { graad: detail });
    case 'licence.grant': return g.tot ? t('vierogen.effect.licentie_tot', { licentie: detail, van: dag(g.datum) || t('bond.vandaag'), tot: dag(g.tot) })
      : t('vierogen.effect.licentie', { licentie: detail, van: dag(g.datum) || t('bond.vandaag') });
    case 'licence.revoke': return t('vierogen.effect.licentie_weg', { licentie: detail });
    case 'member.suspend': return g.tot ? t('vierogen.effect.schorsing_tot', { van: dag(g.datum) || t('bond.vandaag'), tot: dag(g.tot) })
      : t('vierogen.effect.schorsing', { van: dag(g.datum) || t('bond.vandaag') });
    case 'member.unsuspend': return t('vierogen.effect.opheffen');
    case 'user.role': return t('vierogen.effect.rol', { rol: t('start.rolnaam.' + (g.rol || 'viewer')) });
    case 'user.remove': return t('vierogen.effect.gebruiker_weg');
    case 'person.remove': return t(g.wijze === 'verwijderen' ? 'vierogen.effect.lid_weg' : 'vierogen.effect.lid_anoniem');
    case 'person.export': return t(g.paspoorten ? 'vierogen.effect.ledenexport_paspoort' : 'vierogen.effect.ledenexport');
    case 'data.export': return t('vierogen.effect.volledige_export');
  }
  const team = teams[v.payload?.team_id];
  if (!team) return '';
  const nu = balanceOf(team);
  if (v.action_type === 'finance.adjust') return t('vierogen.effect.saldo', { van: eur(nu), naar: eur(v.payload.nieuw_saldo) });
  if (v.action_type === 'finance.writeoff') return t('vierogen.effect.afboeken', { bedrag: eur(nu) });
  if (v.action_type === 'finance.reverse') {
    const terug = Math.max(0, (Number(team.entry_fee_eur) || 0) - (Number(team.discount_eur) || 0) - (Number(team.amount_paid_eur) || 0));
    return t('vierogen.effect.terug', { van: eur(nu), naar: eur(terug), gefactureerd: eur(billedOf(team)) });
  }
  return '';
}

export async function render() {
  const el = document.getElementById('goedkeuringenInhoud');
  if (!el) return;
  koppel(el);
  el.innerHTML = '<div class="spinner"></div>';
  const [lijst, acties, beleid] = await Promise.all([
    sb().from('approval_overzicht').select('*').eq('organisation_id', state.currentOrgId)
      .order('submitted_at', { ascending: false }).limit(100),
    sb().from('dispatch_actions').select('*').order('action_type'),
    sb().from('approval_policies').select('*').eq('organisation_id', state.currentOrgId),
  ]);
  if (lijst.error) {
    el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('vierogen.nietbeschikbaar'))}</span></div>`;
    return;
  }
  const alle = lijst.data || [];
  await laadTeams(alle.filter(v => v.status === 'open'));
  const open = alle.filter(v => v.status === 'open');
  const teBeslissen = open.filter(v => v.mag_beslissen);
  const eigen = open.filter(v => v.submitted_by === state.currentUserEmail.toLowerCase());
  const afgerond = alle.filter(v => v.status !== 'open').slice(0, 30);

  const kaart = (v, knoppen) => `<div class="org-row" style="align-items:flex-start">
      <div style="flex:1;min-width:220px">
        <div style="font-size:13px;font-weight:600">${esc(t('vierogen.handeling.' + v.action_type))} &middot; ${esc(v.object_naam || '')}</div>
        <div style="font-size:12px;color:var(--text-2);margin-top:2px">${esc(effect(v))}</div>
        <div style="font-size:12px;margin-top:4px">&ldquo;${esc(v.reason)}&rdquo;</div>
        <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(t('vierogen.door', { door: v.submitted_by, wanneer: wanneer(v.submitted_at) }))}
          ${v.status === 'open' ? ' &middot; ' + esc(t('vierogen.vervalt', { wanneer: wanneer(v.expires_at) })) : ''}</div>
        ${v.error ? `<div style="font-size:11px;color:var(--red);margin-top:3px">${esc(dispatchFout(v.error))}</div>` : ''}
      </div>
      ${knoppen}
    </div>`;

  const soortenBeleid = Object.fromEntries((beleid.data || []).map(b => [b.action_type, b]));
  const beheer = heeft('org.manage');
  el.innerHTML = `
    <div class="settings-card breed">
      <h3>${esc(t('vierogen.tebeslissen'))}</h3>
      <div class="settings-desc">${esc(t('vierogen.tebeslissen.uitleg'))}</div>
      ${teBeslissen.map(v => kaart(v, `
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          <button type="button" class="btn btn-primary btn-sm" data-actie="goedkeuren" data-id="${v.id}"><i class="ti ti-check"></i> ${esc(t('vierogen.goedkeuren'))}</button>
          <button type="button" class="btn btn-default btn-sm" data-actie="afwijzen" data-id="${v.id}"><i class="ti ti-x"></i> ${esc(t('vierogen.afwijzen'))}</button>
        </div>`)).join('') || `<p style="font-size:12px;color:var(--text-3)">${esc(t('vierogen.niets'))}</p>`}
    </div>
    <div class="settings-card breed">
      <h3>${esc(t('vierogen.eigen'))}</h3>
      ${eigen.map(v => kaart(v, `<button type="button" class="btn btn-ghost btn-sm" data-actie="intrekken" data-id="${v.id}"><i class="ti ti-arrow-back-up"></i> ${esc(t('vierogen.intrekken'))}</button>`)).join('')
        || `<p style="font-size:12px;color:var(--text-3)">${esc(t('vierogen.geeneigen'))}</p>`}
    </div>
    <div class="settings-card breed">
      <h3>${esc(t('vierogen.afgerond'))}</h3>
      ${afgerond.map(v => kaart(v, `<span class="badge ${v.status === 'uitgevoerd' ? 'badge-green' : v.status === 'mislukt' ? 'badge-red' : 'badge-grey'}">${esc(t('vierogen.status.' + v.status))}</span>`)).join('')
        || `<p style="font-size:12px;color:var(--text-3)">${esc(t('vierogen.geenafgerond'))}</p>`}
    </div>
    <div class="settings-card breed">
      <h3>${esc(t('vierogen.beleid'))}</h3>
      <div class="settings-desc">${esc(t('vierogen.beleid.uitleg'))}</div>
      ${(acties.data || []).map(a => {
        const b = soortenBeleid[a.action_type];
        const aan = b ? b.enabled : a.standaard_aan;
        return `<div class="org-row">
          <div style="flex:1;font-size:13px">${esc(t('vierogen.handeling.' + a.action_type))}</div>
          <label style="display:flex;align-items:center;gap:6px;font-size:12px">
            <input type="checkbox" data-beleid="${a.action_type}" ${aan ? 'checked' : ''} ${beheer ? '' : 'disabled'}> ${esc(t('vierogen.beleid.aan'))}</label>
        </div>`;
      }).join('')}
    </div>`;
}

function koppel(el) {
  if (el.dataset.gekoppeld) return;
  el.dataset.gekoppeld = '1';
  el.addEventListener('click', async e => {
    const k = e.target.closest('[data-actie]');
    if (!k) return;
    const id = k.dataset.id;
    if (k.dataset.actie === 'goedkeuren' || k.dataset.actie === 'afwijzen') {
      k.disabled = true;
      const { data, error } = await sb().rpc('beslis', { p_verzoek: id, p_besluit: k.dataset.actie, p_opmerking: null });
      if (error) toast(dispatchFout(error), 'error');
      else toast(t('vierogen.besluit.' + data.status), data.status === 'mislukt' ? 'error' : 'success');
      document.dispatchEvent(new CustomEvent('dispatchklaar'));
    }
    if (k.dataset.actie === 'intrekken') {
      const { error } = await sb().rpc('voorstel_intrekken', { p_verzoek: id });
      if (error) toast(dispatchFout(error), 'error');
    }
    await render();
    await vernieuwTeller();
  });
  el.addEventListener('change', async e => {
    const cb = e.target.closest('[data-beleid]');
    if (!cb) return;
    const { error } = await sb().from('approval_policies').upsert([{
      organisation_id: state.currentOrgId, action_type: cb.dataset.beleid, enabled: cb.checked,
    }]);
    if (error) { toast(dispatchFout(error), 'error'); cb.checked = !cb.checked; return; }
    toast(t('leden.opgeslagen'), 'success');
  });
  document.addEventListener('taalgewijzigd', () => {
    if (document.getElementById('pageGoedkeuringen')?.classList.contains('active')) render();
  });
}
